import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { listAllClients } from '../../api/admin';
import type { ClientCreditsStatus, SubscriptionStatus } from '../../types/db';

type FilterKey = 'all' | 'active' | 'past_due' | 'canceled' | 'expiring' | 'saturated';

const filters: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'Tutti' },
  { key: 'active', label: 'Attivi' },
  { key: 'past_due', label: 'Past due' },
  { key: 'canceled', label: 'Canceled' },
  { key: 'expiring', label: 'In scadenza' },
  { key: 'saturated', label: 'Saturi' },
];

function statusBadgeClass(status: SubscriptionStatus): string {
  switch (status) {
    case 'active':
      return 'bg-emerald-50 text-emerald-700';
    case 'past_due':
      return 'bg-amber-50 text-amber-700';
    case 'canceled':
      return 'bg-rose-50 text-rose-700';
    case 'paused':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}

function isExpiring(periodEnd: string | null): boolean {
  if (!periodEnd) return false;
  const end = new Date(periodEnd);
  const now = new Date();
  const diffDays = (end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays <= 7 && diffDays >= 0;
}

export default function ClientsList() {
  const [clients, setClients] = useState<ClientCreditsStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterKey>('all');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const data = await listAllClients();
        if (!cancelled) setClients(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Errore caricamento clienti');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    return clients.filter((c) => {
      switch (filter) {
        case 'active':
          return c.subscription_status === 'active';
        case 'past_due':
          return c.subscription_status === 'past_due';
        case 'canceled':
          return c.subscription_status === 'canceled';
        case 'expiring':
          return c.subscription_status === 'active' && isExpiring(c.current_period_end);
        case 'saturated':
          return c.is_blocked || (c.monthly_credits_quota > 0 && c.credits_remaining <= c.monthly_credits_quota * 0.1);
        default:
          return true;
      }
    });
  }, [clients, filter]);

  const metrics = useMemo(() => {
    const activeCount = clients.filter((c) => c.subscription_status === 'active').length;
    const mrr = clients.reduce((sum, c) => {
      if (c.subscription_status !== 'active') return sum;
      switch (c.plan_tier) {
        case 'solo':
          return sum + 149;
        case 'studio':
          return sum + 299;
        case 'network':
          return sum + 499;
        case 'group':
          return sum + 999;
        case 'enterprise':
          return sum + 2499;
        default:
          return sum;
      }
    }, 0);
    const totalMinutes = clients.reduce((sum, c) => sum + (c.credits_used ?? 0), 0);
    const nearCapCount = clients.filter(
      (c) => c.monthly_credits_quota > 0 && c.credits_remaining <= c.monthly_credits_quota * 0.1,
    ).length;
    return { activeCount, mrr, totalMinutes, nearCapCount };
  }, [clients]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-rose-700">
        <p className="font-medium">Errore</p>
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <h1 className="text-2xl font-bold text-slate-900">Clienti</h1>

      {/* Metriche */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Attivi</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{metrics.activeCount}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">MRR stimato</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">€{metrics.mrr.toLocaleString('it-IT')}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Minuti usati (mese)</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{Math.round(metrics.totalMinutes)}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <p className="text-xs font-medium text-slate-500">Vicini al cap</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{metrics.nearCapCount}</p>
        </div>
      </div>

      {/* Filtri */}
      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium transition ${
              filter === f.key
                ? 'bg-brand-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-200'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Tabella */}
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="px-4 py-3 font-semibold text-slate-700">Cliente</th>
              <th className="px-4 py-3 font-semibold text-slate-700">Piano</th>
              <th className="px-4 py-3 font-semibold text-slate-700">Stato</th>
              <th className="px-4 py-3 font-semibold text-slate-700">Quota</th>
              <th className="px-4 py-3 font-semibold text-slate-700">Usati</th>
              <th className="px-4 py-3 font-semibold text-slate-700">Rimanenti</th>
              <th className="px-4 py-3 font-semibold text-slate-700">Testate</th>
              <th className="px-4 py-3 font-semibold text-slate-700">Periodo</th>
              <th className="px-4 py-3 font-semibold text-slate-700"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                  Nessun cliente trovato per questo filtro.
                </td>
              </tr>
            )}
            {filtered.map((c) => (
              <tr key={c.client_id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                <td className="px-4 py-3 capitalize text-slate-600">{c.plan_tier ?? '-'}</td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                      c.subscription_status,
                    )}`}
                  >
                    {c.subscription_status}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">{c.monthly_credits_quota}</td>
                <td className="px-4 py-3 text-slate-600">{Math.round(c.credits_used)}</td>
                <td className="px-4 py-3">
                  <span className={c.credits_remaining <= 0 ? 'font-semibold text-rose-600' : 'text-slate-600'}>
                    {Math.round(c.credits_remaining)}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-600">
                  {c.active_productions_count} / {c.max_active_productions}
                </td>
                <td className="px-4 py-3 text-slate-500">
                  {c.current_period_end
                    ? new Date(c.current_period_end).toLocaleDateString('it-IT')
                    : '-'}
                </td>
                <td className="px-4 py-3">
                  <Link
                    to={`/admin/client/${c.client_id}`}
                    className="text-sm font-medium text-brand-600 hover:text-brand-700"
                  >
                    Dettagli
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
